import React from "react";
import { motion } from "framer-motion";
import { ArrowDownIcon } from "lucide-react";
import { heroTaglines } from "../../../data/site";
import { demoCountdown } from "../../../utils/dates";
import { useNow } from "../../../hooks/useNow";
import { Scribble } from "../Scribble";
import { SealBadge } from "../SealBadge";
import { SlideTitle } from "./SlideTitle";
const ease = [0.23, 1, 0.32, 1] as const;
export function IntroSlide({
  active
}: {
  active: boolean;
}) {
  const cd = demoCountdown(useNow());
  return <div className="relative flex h-full flex-col items-center lg:pb-4" data-id="element-411" data-mp="3sbr1yx7wdh36">
      <Scribble variant="wave" className="absolute left-[2%] top-[14%] hidden w-32 md:block" delay={0.6} data-id="element-412" data-mp="augatfixgw4t" />
      <Scribble variant="loop" className="absolute right-0 top-[10%] w-28 md:w-48" delay={0.7} data-id="element-413" data-mp="62y5kf9vg9e1" />
      <Scribble variant="zigzag" className="absolute -left-8 bottom-[16%] w-32 md:w-56" delay={0.8} data-id="element-414" data-mp="3ei85phu7b4j6" />

      {/* Below desktop this group takes the free height and centres itself in it, so there's no big gap
          above or below the title; on desktop it sits at the top as before. */}
      <div className="flex w-full flex-1 flex-col items-center justify-center py-6 lg:flex-none lg:justify-start lg:py-0" data-id="element-415" data-mp="2z47clvye48in">
        <ul className="relative flex flex-wrap items-center justify-center gap-x-10 gap-y-2 rounded-xl border-x border-ink px-6 py-2 text-center text-[12px] font-semibold uppercase tracking-wide lg:mt-12" data-id="element-416" data-mp="33wh075c18whr">
          {heroTaglines.map(t => <li key={t} data-id="element-417" data-mp="2c6mbnaw01au5">{t}</li>)}
        </ul>

        {/* Phones & tablets: four shorter lines so the title can be set much larger. */}
        <SlideTitle lines={["Two countries.", "Eleven builders.", "One week to", "make it real."]} active={active} heading align="center" className="relative mt-6 text-center text-[11vw] md:text-[10vw] lg:hidden" data-id="element-418" data-mp="2l3wkkplcgnvw" />
        {/* Desktop: three lines, sized so "One week to make it real." stays on one line. */}
        <SlideTitle lines={["Two countries.", "Eleven builders.", "One week to make it real."]} active={active} heading align="center" className="relative mt-6 hidden text-center lg:block lg:text-[clamp(2.5rem,min(7vw,10svh),7.5rem)]" data-id="element-419" data-mp="3busev47bzdli" />
      </div>

      <motion.div className="relative lg:mt-auto flex aspect-[2/1] w-full max-w-[760px] lg:max-w-[min(760px,60svh)] flex-col items-center justify-start rounded-t-full bg-sun-light pt-[11%] text-center" initial={{
      y: 60,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      duration: 0.3,
      delay: 0.5,
      ease
    }} data-id="element-420" data-mp="3guev7migh9yj">
        <span className="absolute right-0 top-[38%] block h-24 w-24 sm:h-32 sm:w-32 md:-right-28 md:top-[30%] md:h-44 md:w-44 lg:h-[16svh] lg:max-h-44 lg:w-[16svh] lg:max-w-44" data-id="element-421" data-mp="32vowk2fzn282">
          <SealBadge ring="DEMO DAY · LIVE PITCHES · DEMO DAY · LIVE PITCHES · " center={<span className="text-[clamp(1.2rem,2.4vw,2.2rem)] tracking-normal" data-id="element-423" data-mp="25eq5indthwu1">
                Oct
                <br data-id="element-424" data-mp="kvmokjbccrmc" />
                13
              </span>} className="h-full w-full" data-id="element-422" data-mp="2acrxxyx4v8i6" />
        </span>
        <p className="font-display text-[clamp(2.4rem,6vw,4.5rem)] uppercase leading-none" data-id="element-425" data-mp="az1jxz38hij1">
          {cd.done ? "We're live" : `${cd.days}d ${cd.hours}h`}
        </p>
        <p className="mt-2 max-w-xs text-[12px] font-semibold uppercase tracking-wide" data-id="element-426" data-mp="fvrxofrkg21a">
          {cd.done ? "Demo Day is happening now" : "until Demo Day — five teams from Brazil & India pitch to investors"}
        </p>
        <a href="#teams" tabIndex={active ? 0 : -1} aria-label="Scroll to the teams" className="mt-6 rounded-full p-2 lg:mt-3 transition-transform duration-150 hover:translate-y-1" data-id="element-427" data-mp="umr1ldhgi2ej">
          <ArrowDownIcon className="h-6 w-6" data-id="element-428" data-mp="3fcq4v9nk3nat" />
        </a>
      </motion.div>
    </div>;
}